import {
  PermissionContext,
  PermissionContextOrganization,
  PermissionContextUser,
  permissionsOperations,
} from '@ardoq/access-control';
import { APICurrentUser } from '@ardoq/api-types';
import { spreadMap, toPersistentStream } from '@ardoq/rxbeach';
import { isEqual } from 'lodash';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
} from 'rxjs';
import { currentUser$ } from '../currentUser/currentUser$';
import { currentUserPermissions$ } from './currentUserPermissions$';
import { CurrentUserPermissionsState } from './types';

const toPermissionContext = (
  user: PermissionContextUser | null,
  {
    confidentialFieldPermissions,
    resourcePermissions,
  }: CurrentUserPermissionsState
): PermissionContext => {
  if (!user) {
    // It is easy to forget, but public presentations can be viewed by
    // unauthenticated users, so confidential field checks need to be available
    // even when the user is unauthenticated.
    return permissionsOperations.createUnauthenticatedPermissionContext(
      confidentialFieldPermissions
    );
  }
  return permissionsOperations.toPermissionContext(
    user,
    resourcePermissions,
    confidentialFieldPermissions
  );
};

const toPermissionContextOrganization = (
  organization: APICurrentUser['organization']
): PermissionContextOrganization => ({
  _id: organization._id,
  label: organization.label,
  role: organization.role,
  expired: organization.expired,
  'custom-domain': organization['custom-domain'],
  'is-trial': organization['is-trial'],
});

const toPermissionContextUser = (
  user: APICurrentUser | null
): PermissionContextUser | null => {
  if (!user) return null;
  return {
    _id: user._id,
    email: user.email,
    writeAccess: user.writeAccess,
    roles: user.roles,
    groups: user.groups,
    organization: toPermissionContextOrganization(user.organization),
    privileges: user.privileges,
    impersonatedBy: user.impersonatedBy,
  };
};

const currentUserPermissionContext$: Observable<PermissionContext> =
  combineLatest([
    currentUser$.pipe(
      map(toPermissionContextUser),
      distinctUntilChanged((a, b) => isEqual(a, b))
    ),
    currentUserPermissions$,
  ]).pipe(
    spreadMap(toPermissionContext),
    shareReplay({ bufferSize: 1, refCount: true })
  );

const persistentCurrentUserPermissionContext$ = toPersistentStream(
  'currentUserPermissionContext$',
  currentUserPermissionContext$,
  permissionsOperations.createEmptyPermissionContext()
);

/**
 * @deprecated prefer currentUserPermissionContext$
 */
export const getCurrentUserPermissionContext = (): PermissionContext => {
  return persistentCurrentUserPermissionContext$.state;
};

export { currentUserPermissionContext$ };
